import React from 'react';
import { Calendar } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { eventsData, categoryColors } from '../../../data/eventsData';

const getImportanceBadge = (importance: number): { label: string; style: string } => {
  if (importance >= 5) return { label: 'CRITICAL', style: 'bg-data-red text-white' };
  if (importance >= 4) return { label: 'HIGH', style: 'bg-data-green text-black' };
  if (importance >= 3) return { label: 'MEDIUM', style: 'bg-data-amber text-black' };
  return { label: 'LOW', style: 'bg-gray-600 text-white' };
};

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getMonthYear = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
};

interface EconomicCalendarPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const EconomicCalendarPanel: React.FC<EconomicCalendarPanelProps> = ({ isMinimized, onMinimize }) => {
  const { economicCalendar } = eventsData;

  // Group by month
  const groupedEvents = economicCalendar.reduce((acc, event) => {
    const monthYear = getMonthYear(event.date);
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(event);
    return acc;
  }, {} as Record<string, typeof economicCalendar>);

  const next30Days = economicCalendar.filter(e => {
    const date = new Date(e.date);
    const now = new Date('2024-12-12');
    const diff = (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 30;
  }).length;

  const criticalEvents = economicCalendar.filter(e => e.importance >= 4).length;
  const months = Object.keys(groupedEvents);

  return (
    <Panel title="ECONOMIC CALENDAR" subtitle="DATA RELEASES" status="static" isMinimized={isMinimized} onMinimize={onMinimize}>
      <div className="flex flex-col h-full p-3">
        {/* Hero Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-white">{economicCalendar.length}</div>
            <div className="text-xs text-gray-500">Total Events</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-amber">{next30Days}</div>
            <div className="text-xs text-gray-500">Next 30 Days</div>
          </div>
          <div className="bg-terminal-bg/60 rounded-lg p-3 text-center border border-terminal-border/50">
            <div className="text-2xl font-mono font-bold text-data-green">{criticalEvents}</div>
            <div className="text-xs text-gray-500">High Impact</div>
          </div>
        </div>

        {/* Month Tabs */}
        <div className="flex items-center gap-2 mb-3">
          {months.slice(0, 4).map((month, i) => (
            <button
              key={month}
              className={`px-3 py-1.5 text-xs rounded transition-all ${
                i === 0
                  ? 'bg-data-green/20 text-data-green border border-data-green/30'
                  : 'bg-terminal-bg/40 text-gray-400 hover:text-white border border-transparent'
              }`}
            >
              {month}
            </button>
          ))}
        </div>

        {/* Event Cards */}
        <div className="flex-1 overflow-auto custom-scrollbar space-y-2">
          {Object.entries(groupedEvents).slice(0, 2).map(([month, events]) => (
            <React.Fragment key={month}>
              {events.slice(0, 5).map((event, idx) => {
                const badge = getImportanceBadge(event.importance);
                return (
                  <div
                    key={idx}
                    className={`bg-terminal-bg/40 rounded-lg p-3 border ${
                      event.importance >= 4 ? 'border-data-green/30' : 'border-terminal-border/30'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-mono">{formatDate(event.date)}</span>
                        <span className="text-sm font-semibold text-white">{event.event}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${badge.style}`}>
                        {badge.label}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span
                        className="text-xs"
                        style={{ color: categoryColors[event.category] }}
                      >
                        {event.category}
                      </span>
                      <div className="flex items-center gap-4">
                        <span className="text-gray-500">
                          Prev: <span className="text-white font-mono">{event.previous}</span>
                        </span>
                        <span className="text-gray-500">
                          Fcst: <span className="text-data-green font-mono font-bold">{event.forecast}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.NATEX} showLabel size="xs" />
          <span className="text-xs text-gray-500">Dec 2025</span>
        </div>
      </div>
    </Panel>
  );
};

export default EconomicCalendarPanel;
